import _ from 'lodash';
import Collection from '@skyjs/collection';

export default function generateClassCode(spec, options = {}) {
  const { className, subclassName, moduleName, imports } = spec;
  const { fields, methods } = setDefaultValues(spec);

  const constructorMethod = getConstructorMethod(fields, subclassName);
  if (constructorMethod) {
    methods.push(constructorMethod);
  }

  const staticFields = fields.filter({ isStatic: true });

  const members = methods
    .orderBy(m => (m.isStatic ? 0 : m.name === 'constructor' ? 1 : 2))
    .map(getMethodCode);

  let body = members.join('\n\n');
  if (members.length > 0) {
    body = '\n' + body + '\n';
  }

  let classDeclaration = 'class ' + className;
  if (subclassName) {
    classDeclaration += ' extends ' + subclassName;
  }

  let importStatements = getImportStatements(imports, fields, methods);
  if (!_.isEmpty(importStatements)) {
    importStatements += '\n\n';
  }

  const classAssignments =
    staticFields.length > 0
      ? '\n\n' + staticFields.map(getStaticFieldCode(className)).join('\n') + '\n'
      : '';

  let code = `${importStatements}${classDeclaration} {${body}}${classAssignments}

exports.default = ${className};
module.exports = exports.default;`;

  if (options.sourceURL) {
    code += `\n\n//# sourceURL=${moduleName}.js`;
  }

  return code;
}

const setDefaultValues = spec => {
  const { fields, methods } = spec;
  return {
    ...spec,
    fields: new Collection(
      ..._.map(fields, field => ({ ...field, isStatic: field.isStatic === true }))
    ),
    methods: new Collection(
      ..._.map(methods, method => ({ ...method, isStatic: method.isStatic === true }))
    )
  };
};

const getMethodParams = param => {
  if (_.isEmpty(param)) return '';
  if (_.isArray(param)) return '{ ' + _.map(param, 'name').join(', ') + ' }';
  return param.name;
};

const getMethodDeclaration = method => {
  let type = method.isStatic ? 'static ' : '';
  type += method.isAsync ? 'async ' : '';
  type += method.isGetter ? 'get ' : '';
  const params = _.map(method.params, getMethodParams).join(', ');
  return `${type}${method.bracketNotation ? `['${method.name}']` : method.name}(${params})`;
};

const getFieldValue = field => field.valueExpression || JSON.stringify(field.value);

const getConstructorMethod = (fields, subclassName) => {
  const instanceFields = fields
    .filter(f => !f.isStatic)
    .filter(f => !_.isNil(f.value) || !_.isNil(f.valueExpression));
  if (instanceFields.length === 0) return;
  let body = subclassName ? 'super();\n' : '';
  body += instanceFields.map(field => `this.${field.name} = ${getFieldValue(field)};`).join('\n');
  return { name: 'constructor', isStatic: false, body };
};

const getStaticFieldCode = className => field => {
  const comment = field.comment ? '// ' + field.comment + '\n  ' : '';
  return `${comment}${className}.${field.name} = ${getFieldValue(field)};`;
};

const getMethodCode = method => {
  const lines = _.split(method.body, '\n');
  const comment = method.comment ? '// ' + method.comment + '\n  ' : '';
  return `  ${comment}${getMethodDeclaration(method)} {\n${lines
    .map(line => '    ' + line)
    .join('\n')}\n  }`;
};

const getImportStatements = (root = [], fields, methods) => {
  let importStatements = [
    ...root,
    ...fields.flatMap('imports').compact(),
    ...methods.flatMap('imports').compact()
  ];

  importStatements = _.uniqBy(
    importStatements,
    ({ defaultExport, namedExports }) => defaultExport || namedExports.join(', ')
  );

  return importStatements
    .map(({ defaultExport, namedExports, moduleName }) => {
      const moduleExports = defaultExport || `{ ${namedExports.join(', ')} }`;
      return `const ${moduleExports} = require('${moduleName}');`;
    })
    .join('\n');
};
